import { forwardRef, ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Auth, { AuthRef } from './Auth';
import ToastyConfig from './components/toasty';
import Payment from './Payment';
import { IProduct, IShowPaymentOptions, IUser, SDKProps } from './type';
import { getMe, pingActivity } from './core/auth';
import { getProductByCode } from './core/payment';
import { resolveSdkUrls } from './core/env';
import { ensureTrackingScripts, trackLoginSuccess, trackRegisterSuccess } from './core/tracking';
import { persistPaymentCloseUrlFromSearch } from './core/close-payment-window';
import {
  getEffectiveAccessToken,
  getGameUrlAccessToken,
  initGameTokenSession,
  isGameTokenSession,
  persistSdkAuthContext,
  resolveSdkAuthContext,
} from './core/sdk-auth';
import { ensureStandalonePaymentPopup } from './core/standalone-payment-popup';
import VerifyPhoneGroup from './components/auth/verify-phone';
import WelcomePopup from './components/welcome-popup';
import ErrorPopup from './components/error-popup';
import StandalonePaymentHandoff from './components/payment/standalone-handoff';
import AgeRatingBadge from './components/age-rating-badge';
import {
  REQUIRE_VERIFY_PHONE_AFTER_LOGIN_DELAY_MS,
  LAST_LOGIN_TS_STORAGE_PREFIX,
  LAST_DISMISS_VERIFY_TS_STORAGE_PREFIX,
  DISMISS_VERIFY_PHONE_COOLDOWN_MS,
} from './constants/index';
import { extractErrorMessage } from './core/utils';

const LAST_LOGIN_TS_KEY = (userId: string | number) => `${LAST_LOGIN_TS_STORAGE_PREFIX}:${userId}`;
/** Per-user key lưu timestamp lần gần nhất user click ✕ tắt popup verify SĐT (để cool down). */
const LAST_DISMISS_VERIFY_TS_KEY = (userId: string | number) =>
  `${LAST_DISMISS_VERIFY_TS_STORAGE_PREFIX}:${userId}`;

const App = forwardRef(({
  serviceHref = '/dieu-khoan-dich-vu',
  privacyHref = 'https://nap.joyplay.vn/chinh-sach-bao-mat',
  productCode = '',
  mode,
  //apiEndpoint = 'https://sdkapi.joygames.vn',
  on = () => { },
  onSdkReady = () => { }
}: SDKProps, ref) => {
  const { baseUrl, apiEndpoint } = resolveSdkUrls();
  const [isPaymentPopupChild] = useState(() => ensureStandalonePaymentPopup());

  const [openAuthModal, setShowAuth] = useState(false);
  const [openPaymentModal, setShowPayment] = useState<boolean>(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [accessToken, setAccessToken] = useState<string>(() => getGameUrlAccessToken() || '');
  const [user, setUser] = useState<IUser | null>(null);
  const [product, setProduct] = useState<IProduct>();
  const [openPhone, setOpenPhone] = useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [isNewLogin, setIsNewLogin] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState<string | null>(null);
  const [errorPopupDuration, setErrorPopupDuration] = useState<number>(5000);
  const paymentOptions = useRef<IShowPaymentOptions>({} as any);
  const authRef = useRef<AuthRef>(null);
  const trackingProductCodesInited = useRef<Set<string>>(new Set());
  const welcomedUserIds = useRef<Set<string>>(new Set());
  const isLoggingOutRef = useRef(false);
  const autoLoginGuestTimerRef = useRef<number | null>(null);
  const welcomeDismissedRef = useRef(false);
  const userRef = useRef<IUser | null>(null);

  // TODO - create shared lib to get / load access token
  // then we can access everywhere

  /** Show error notification kiểu WelcomePopup — cho login google/facebook/email hoặc lỗi API khác. */
  const showSdkErrorNotification = (e: any, fallback?: string, durationMs = 5000) => {
    const msg = extractErrorMessage(e, fallback);
    setErrorPopupDuration(durationMs);
    setErrorPopupMessage(msg);
  };

  const loginSuccess = (data: any) => {
    // Phiên mở bằng token game trên URL — không cho guest/login web ghi đè Authorization
    if (isGameTokenSession()) {
      return;
    }
    // Guard: Vừa nhấn logout (1.5s trước đó — ko cho spawn guest / login mới show welcome
    // vào thời gian ngắn trong khi logout callback / states chưa reset về null.
    if (isLoggingOutRef.current) {
      return;
    }
    setAccessToken(data.accessToken);
    localStorage.setItem('joygamesAccessToken', data.accessToken);
    setUser(data.user);
    userRef.current = data.user;
    setLoggedIn(true);
    const userId = String(data.user?.id ?? '');
    if (userId && !welcomedUserIds.current.has(userId)) {
      setIsNewLogin(true);
      setShowWelcomePopup(true);
    } else {
      setIsNewLogin(false);
      setShowWelcomePopup(false);
    }

    if (data.user?.id) {
      try {
        localStorage.setItem(LAST_LOGIN_TS_KEY(data.user.id), String(Date.now()));
      } catch { /* ignore */ }
    }

    void pingActivity(data.accessToken, apiEndpoint, product?.code || productCode);
    const tracking = {
      facebookPixelId: product?.facebookPixelId,
      gtmContainerId: product?.gtmContainerId,
      tiktokPixelCode: product?.tiktokPixelCode,
    };
    trackLoginSuccess({
      loginMethod: data.loginMethod || data.user?.registerBy,
      userId: data.user?.id,
      productCode: product?.code || productCode,
      tracking,
    });
    if (data.isNewUser) {
      trackRegisterSuccess({
        loginMethod: data.loginMethod || data.user?.registerBy,
        userId: data.user?.id,
        productCode: product?.code || productCode,
        tracking,
      });
    }
    on('loginSuccess', data);
  };

  const setAccessTokenSafe = (token: string) => {
    if (isGameTokenSession()) {
      setAccessToken(getGameUrlAccessToken()!);
      return;
    }
    setAccessToken(token);
  };

  const logout = () => {
    // Double-click guard (anti gọi nhiều lần liên tục làm spawn lại guest)
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    if (autoLoginGuestTimerRef.current != null) {
      window.clearTimeout(autoLoginGuestTimerRef.current);
      autoLoginGuestTimerRef.current = null;
    }
    const prevUserId = String(user?.id ?? '');
    if (prevUserId) {
      welcomedUserIds.current.delete(prevUserId);
    }
    setUser(null);
    userRef.current = null;
    setAccessToken('');
    localStorage.removeItem('joygamesAccessToken');
    setLoggedIn(false);
    setShowWelcomePopup(false);
    setIsNewLogin(false);
    setOpenPhone(false);
    setShowAuth(false);
    // Bật cờ 1500ms — đảm bảo timeout autoLoginGuestWeb 100ms và các callback
    // đến sau (Auth onLogin success) không được trigger guest / show welcome nữa
    window.setTimeout(() => {
      isLoggingOutRef.current = false;
    }, 1500);
    on('logoutSuccess', {
      accessToken: null,
      user: null
    });
  };

  useImperativeHandle(ref, () => ({
    openPhoneModal(show = true) {
      setOpenPhone(show)
    },
    /**
     * show auth modal
     * @param show
     * @returns
     */
    openAuthModal(show = true) {
      if (show && loggedIn) {
        if (window.confirm('User is already logged in, do you want to log out?')) {
          logout()
        }
        return;
      }

      setShowAuth(show);
    },

    // TODO - define payment item here such as serverId, gameId, etc...
    openPaymentModal(options: IShowPaymentOptions) {
      if (options.skipLogin) {
        const ctx = resolveSdkAuthContext(
          options.accessToken,
          product?.code || productCode
        );
        persistSdkAuthContext(ctx);
        setAccessTokenSafe(options.accessToken);
        setLoggedIn(true);
      }
      if (!loggedIn && !options.skipLogin) {
        on('openPaymentModalError', {
          loggedIn,
          user,
          message: 'User not logged in!'
        });
        return;
      }
      paymentOptions.current = options;
      // show popup and load
      setShowPayment(true);
    },

    /**
     * check if user is logged in or not
     * @returns boolean
     */
    isLoggedIn() {
      return loggedIn;
    },

    /**
     * get the current logged in user
     * @returns
     */
    getUser() {
      return user;
    },

    /**
    * get the product info
    * @returns
    */
    getProduct() {
      return product;
    },

    /**
     * log out current user
     */
    logout,

    /**
     * Show error notification (same style as Welcome popup, red accent),
     * accepts string message or axios error object.
     * Duration default 5s, pass 0 to disable auto close.
     */
    showErrorNotification(errorOrMessage: any, durationMs = 5000) {
      showSdkErrorNotification(errorOrMessage, undefined, durationMs);
      return {
        close() { setErrorPopupMessage(null); },
      };
    },
  }));

  useEffect(() => {
    persistPaymentCloseUrlFromSearch();
  }, []);

  useEffect(() => {
    if (!isPaymentPopupChild || !product) return;
    const key = String(product.code || '');
    if (!key) return;
    if (trackingProductCodesInited.current.has(key)) return;
    trackingProductCodesInited.current.add(key);
    ensureTrackingScripts({
      facebookPixelId: product.facebookPixelId,
      gtmContainerId: product.gtmContainerId,
      tiktokPixelCode: product.tiktokPixelCode,
    });
  }, [isPaymentPopupChild, product]);

  useEffect(() => {
    const ready = async () => {
      try {
        const gameToken = getGameUrlAccessToken();
        const storedToken = isGameTokenSession() ? null : localStorage.getItem('joygamesAccessToken');

        const productResp = await getProductByCode(productCode, apiEndpoint);
        const data = productResp?.data;
        if (data) {
          data.mode = mode;
          setProduct(data);
        }

        if (gameToken) {
          initGameTokenSession(productCode);
          setAccessTokenSafe(gameToken);
          setLoggedIn(true);
          onSdkReady(!data ? 'Product was not found' : 'SDK is ready');
          return;
        }

        let user: IUser | null = null;
        if (storedToken) {
          try {
            user = await getMe(storedToken, apiEndpoint);
          } catch {
            localStorage.removeItem('joygamesAccessToken');
          }
        }

        // Có token trong localStorage → restore session + hiện welcome (F5 / reload)
        if (storedToken && user) {
          setUser(user);
          userRef.current = user;
          setAccessToken(storedToken);
          setLoggedIn(true);
          const userId = String(user.id ?? '');
          if (userId && !welcomedUserIds.current.has(userId)) {
            setIsNewLogin(true);
            setShowWelcomePopup(true);
          } else {
            setIsNewLogin(false);
            setShowWelcomePopup(false);
          }

          if (user.id) {
            try {
              const prev = localStorage.getItem(LAST_LOGIN_TS_KEY(user.id));
              if (!prev) {
                localStorage.setItem(LAST_LOGIN_TS_KEY(user.id), String(Date.now()));
              }
            } catch { /* ignore */ }
          }

          void pingActivity(storedToken, apiEndpoint, data?.code || productCode);
          on('loginSuccess', { accessToken: storedToken, user });
        } else if (data?.autoLoginGuestWeb === 1) {
          if (autoLoginGuestTimerRef.current != null) {
            window.clearTimeout(autoLoginGuestTimerRef.current);
            autoLoginGuestTimerRef.current = null;
          }
          const handle = window.setTimeout(async () => {
            autoLoginGuestTimerRef.current = null;
            try {
              // 3 ĐIỀU KIỆN ĐỦ SAU 100ms MỚI ĐƯỢC SPAWN GUEST:
              //   1) Không phải session game token URL
              //   2) Không đang trong quá trình logout (1.5s guard flag)
              //   3) Hiện tại chưa có user (chưa ai login / đã logout rồi mới cần guest
              if (isGameTokenSession()) return;
              if (isLoggingOutRef.current) return;
              if (authRef.current && !userRef.current) {
                await authRef.current.triggerQuickLogin();
              }
            } catch (e) {
              console.error('Auto guest login failed:', e);
            }
          }, 100);
          autoLoginGuestTimerRef.current = handle as unknown as number;
        }
        onSdkReady(!data ? 'Product was not found' : 'SDK is ready');
      } catch (e) {
        console.error('SDK initialization error:', e);
      }
    };

    ready();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productCode, apiEndpoint]);

  const shouldShowVerifyPhone = (u: IUser | null | undefined, p?: IProduct): boolean => {
    if (!u) return false;
    const needsVerify = !u.isPhoneNumberVerified || !u.phoneNumber;
    if (!needsVerify) return false;
    // requirePhoneWebActive = 1 → bỏ qua step verify (logic cũ)
    if (p?.requirePhoneWebActive === 1) return false;
    // isOpenPopupUpdateInfoH5 = 0 → tắt popup H5 (default bật)
    if (p?.isOpenPopupUpdateInfoH5 === 0) return false;

    try {
      // ✅ NGƯỜI DÙNG CHỦ ĐỘNG CLICK ✕ TẮT POPUP GẦN ĐÂY (cool down 12 tiếng)
      //    → TRẢ FALSE NGAY, KHÔNG HỎI LẠI LIÊN TỤC (fix bug: click x → re-show ngay lập tức)
      if (u.id) {
        const lastDismissRaw = localStorage.getItem(LAST_DISMISS_VERIFY_TS_KEY(u.id));
        const lastDismissTs = lastDismissRaw ? Number(lastDismissRaw) || 0 : 0;
        if (lastDismissTs) {
          const cool = Number(DISMISS_VERIFY_PHONE_COOLDOWN_MS) || 0;
          if (Date.now() - lastDismissTs < cool) return false;
        }
      }

      const lastLoginRaw = u.id ? localStorage.getItem(LAST_LOGIN_TS_KEY(u.id)) : null;
      const lastLoginTs = lastLoginRaw ? Number(lastLoginRaw) || 0 : 0;
      // Chưa có record last-login (trường hợp edge-case) → lưu now làm gốc, chưa hiện
      if (!lastLoginTs) {
        if (u.id) localStorage.setItem(LAST_LOGIN_TS_KEY(u.id), String(Date.now()));
        return false;
      }
      const delay = Number(REQUIRE_VERIFY_PHONE_AFTER_LOGIN_DELAY_MS) || 0;
      const reached = (Date.now() - lastLoginTs) >= delay;
      if (!reached) return false;

      // Đủ delay + user chưa dismiss/cool down hết → hiện popup xác thực
      return true;
    } catch {
      // Lỗi đọc localStorage → fallback cho hiện luôn (không chặn user vô lý)
      return true;
    }
  };

  const getRemainingUntilVerifyMs = (u: IUser | null | undefined): number => {
    if (!u?.id) return 0;
    try {
      const lastLoginRaw = localStorage.getItem(LAST_LOGIN_TS_KEY(u.id));
      const lastLoginTs = lastLoginRaw ? Number(lastLoginRaw) || 0 : 0;
      if (!lastLoginTs) return Number(REQUIRE_VERIFY_PHONE_AFTER_LOGIN_DELAY_MS) || 0;
      const delay = Number(REQUIRE_VERIFY_PHONE_AFTER_LOGIN_DELAY_MS) || 0;
      const remain = Math.max(0, delay - (Date.now() - lastLoginTs));
      return remain;
    } catch {
      return 0;
    }
  };

  useEffect(() => {
    if (!user) return;
    const needsVerify = !user.isPhoneNumberVerified || !user.phoneNumber;
    if (!needsVerify) return;
    // requirePhoneWebActive = 1 → bỏ qua step verify (logic cũ)
    if (product?.requirePhoneWebActive === 1) return;
    // isOpenPopupUpdateInfoH5 = 0 → tắt popup H5 (default bật)
    if (product?.isOpenPopupUpdateInfoH5 === 0) return;

    // Đủ delay → hiện popup lập tức
    if (shouldShowVerifyPhone(user, product)) {
      setOpenPhone(true);
      return;
    }

    // Chưa đủ delay → đặt setTimeout để vào game luôn, sau 10p (hoặc giá trị config) mới hiện popup
    const remain = getRemainingUntilVerifyMs(user);
    if (remain <= 0) return;
    if (remain > 24 * 60 * 60 * 1000) return; // clamp 24h (tránh số vô lý)

    const handle = window.setTimeout(() => {
      if (shouldShowVerifyPhone(user, product)) {
        setOpenPhone(true);
      }
    }, remain);

    return () => {
      if (handle) window.clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, product]);

  const openVerifyIfNeeded = () => {
    if (!user) return;
    // User click badge AgeRating (chủ động mở verify) → bỏ qua cool down dismiss 12h,
    // xóa flag dismissed để shouldShowVerifyPhone() trên re-render cũng trả về true.
    try { if (user.id) localStorage.removeItem(LAST_DISMISS_VERIFY_TS_KEY(user.id)); } catch { /* ignore */ }
    if (!shouldShowVerifyPhone(user, product)) return;
    setOpenPhone(true);
  };

  let content: ReactNode = null;

  if (!isPaymentPopupChild) {
    content = (
      <main className="main-container">
        <StandalonePaymentHandoff />
      </main>
    );
  } else if (!product) {
    content = null;
  } else if (user && openPhone) {
    content = (
      <main>
        <div className='modal_bg' />
        <div className='modal_wrapper small'>
          <VerifyPhoneGroup
            user={user}
            product={product}
            onError={(e: any) => on('verifyPhoneError', e)}
            onFinish={({ phoneNumber, name, birthdayDate }) => {
              setUser((_user: any) => {
                const next = {
                  ..._user,
                  isPhoneNumberVerified: true,
                  phoneNumber,
                  name,
                  birthdayDate
                } as any;
                userRef.current = next;
                return next;
              });
              // User đã verify thành công → xóa flag dismissed (nếu có) để session sau không bị hiểu lầm
              try { if (user.id) localStorage.removeItem(LAST_DISMISS_VERIFY_TS_KEY(user.id)); } catch { /* ignore */ }
              setOpenPhone(false);
            }}
            onClickClose={() => {
              // User chủ động click ✕ TẮT popup (chưa muốn verify bây giờ)
              //   1) Lưu timestamp vào localStorage → cool down 12 tiếng, KHÔNG hiện lại liên tục
              //   2) Đóng popup ngay lập tức → user vào chơi game tiếp bình thường
              //   3) Gọi SDK API openPhoneModal(false) cho imperative handle sync
              try { if (user.id) localStorage.setItem(LAST_DISMISS_VERIFY_TS_KEY(user.id), String(Date.now())); } catch { /* ignore */ }
              setOpenPhone(false);
              if (typeof window !== 'undefined' && (window as any).JoyGameSDKInstance) {
                try { (window as any).JoyGameSDKInstance.openPhoneModal(false); } catch { /* ignore */ }
              }
              on('verifyPhoneDismissed', { userId: user.id, productCode: product?.code || productCode });
            }}
            apiEndpoint={apiEndpoint}
            accessToken={accessToken} />
        </div>
        <ToastyConfig />
      </main>
    );
  } else {
    // TODO - check and use class name for sdk
    content = (
      <main className="main-container">
        {((openAuthModal) || (openPaymentModal && product)) && <div className='modal_bg' />}
        {showWelcomePopup && user && isNewLogin && <WelcomePopup
          user={user}
          onClose={() => {
            if (welcomeDismissedRef.current) return;
            welcomeDismissedRef.current = true;
            setShowWelcomePopup(false);
            setIsNewLogin(false);
            const uid = String(user?.id ?? '');
            if (uid) welcomedUserIds.current.add(uid);
            window.setTimeout(() => {
              welcomeDismissedRef.current = false;
            }, 800);
          }}
          onExit={() => {
            if (welcomeDismissedRef.current) return;
            welcomeDismissedRef.current = true;
            setShowWelcomePopup(false);
            setIsNewLogin(false);
            const uid = String(user?.id ?? '');
            if (uid) welcomedUserIds.current.add(uid);
            logout();
            window.setTimeout(() => {
              welcomeDismissedRef.current = false;
            }, 2000);
          }}
        />}
        {/* Auto guest chỉ khi KHÔNG có accessToken game trên URL */}
        {product && !isGameTokenSession() && (
          <div style={{ display: 'none' }}>
            <Auth
              ref={authRef}
              product={product}
              privacyHref={privacyHref}
              apiEndpoint={apiEndpoint}
              onClickClose={() => {}}
              onLoginSuccess={loginSuccess}
              onLoginError={(e: any) => {
                // Auto guest → silent, không show popup lỗi (tránh spam user không làm gì cả).
                on('loginError', e);
              }}
            />
          </div>
        )}
        {openAuthModal && product && <Auth
          product={product}
          privacyHref={privacyHref}
          apiEndpoint={apiEndpoint}
          onClickClose={() => setShowAuth(false)}
          onLoginSuccess={loginSuccess}
          onLoginError={(e: any) => {
            // ✅ User chủ động bấm đăng nhập (email / google / facebook / zalo / register)
            // có lỗi từ API → show notification đỏ giống WelcomePopup,
            // lấy message từ e.response.data.message (NestJS chuẩn như ảnh statusCode 400).
            showSdkErrorNotification(e);
            on('loginError', e);
          }}
        />}
        {openPaymentModal && <Payment
          baseUrl={baseUrl}
          {...paymentOptions.current}
          productId={product.code}
          serviceHref={serviceHref}
          privacyHref={privacyHref}
          accessToken={getEffectiveAccessToken(accessToken)}
          apiEndpoint={apiEndpoint}
          onPaymentSuccess={(d: any) => {
            on('paymentSuccess', d);
          }}
          onPaymentError={(e: any) => {
            showSdkErrorNotification(e, 'Thanh toán thất bại, vui lòng thử lại.');
            on('paymentError', e);
          }}
          onClickClose={() => setShowPayment(false)}
        />}
        <ToastyConfig />
      </main>
    );
  }

  return (
    <>
      <AgeRatingBadge onTap={openVerifyIfNeeded} />
      {/* Error notification — same top-slide pattern as WelcomePopup, 1 place cho mọi branch.
          User chủ động login (email / google / facebook / zalo / register) / payment bị error
          từ API → hiện đây (ví dụ ảnh statusCode 400 "Tài khoản đang được yêu cầu xóa tài khoản…") */}
      {errorPopupMessage && (
        <ErrorPopup
          message={errorPopupMessage}
          onClose={() => setErrorPopupMessage(null)}
          duration={errorPopupDuration}
        />
      )}
      {content}
    </>
  );
});

export default App;
